import { forwardRef } from 'react';
import { cn } from '@/shared/utils/utils';

type Variant = 'primary' | 'secondary';

interface FullPageCtaProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
}

const baseClasses =
  'group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3 text-sm font-semibold tracking-tight transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 cursor-pointer [&_svg]:pointer-events-none [&_svg]:shrink-0';

const variantClasses: Record<Variant, string> = {
  primary: cn(
    'bg-gradient-to-br from-slate-900 to-slate-800 text-white',
    'shadow-[0_10px_24px_-12px_rgba(15,23,42,0.55)]',
    'hover:from-slate-800 hover:to-slate-700 hover:-translate-y-0.5',
    'hover:shadow-[0_14px_28px_-12px_rgba(15,23,42,0.6)]',
    'active:translate-y-0 active:shadow-[0_6px_18px_-10px_rgba(15,23,42,0.5)]'
  ),
  secondary: cn(
    'border border-slate-200 bg-white/90 text-slate-700 backdrop-blur-sm',
    'shadow-sm hover:bg-white hover:border-slate-300 hover:text-slate-900',
    'hover:-translate-y-0.5 hover:shadow-md'
  ),
};

export const FullPageCta = forwardRef<HTMLButtonElement, FullPageCtaProps>(
  ({ className, variant = 'primary', children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        className={cn(baseClasses, variantClasses[variant], className)}
        {...props}
      >
        {children}
      </button>
    );
  }
);
FullPageCta.displayName = 'FullPageCta';
